/**
 * @fileoverview Flagged Transactions — static per-head configuration
 * @module jobs/summarization/agents/flaggedTransactions/headConfigs
 *
 * One config per managed analysis head (MOR-514 sub-tickets), consumed by:
 *   - `headFactory.ts` — builds each head's ManagedAgentManifest
 *   - `debounce.ts` — maps headKey → manifestId when scheduling runs
 *   - `runner.ts` — FLAGGED_UPSTREAM drives sibling-agent reactivity
 *   - the workspace UI (labels and ordering via publicQueries)
 */

import type { FlaggedHeadKey } from "@cvx/jobs/summarization/flaggedTransactions/schema";
import type { AppointmentType } from "@cvx/jobs/schema";
import type { Doc } from "@cvx/_generated/dataModel";
import { isPersonalInsolvencyAppointmentType } from "@cvx/lib/appointmentTypes";

export const FLAGGED_MASTER_MANIFEST_ID = "sum_flagged_master";
export const FLAGGED_MASTER_AGENT_NAME = "Flagged Transactions Master";

/**
 * Which statutory regime a head belongs to (MOR-1254): the thirteen
 * Corporations Act heads, or the three Bankruptcy Act heads. A job only
 * schedules heads for its own regime — see `headsForJob`.
 */
export type FlaggedRegime = "corporate" | "personal";

export interface FlaggedHeadConfig {
  headKey: FlaggedHeadKey;
  /**
   * Which statutory regime the head belongs to (MOR-1254). Gated at schedule
   * time: a corporate job never creates rows for a personal head and vice
   * versa, so no render-time filter is needed.
   */
  regime: FlaggedRegime;
  /** Manifest / summarizationTasks agentId. */
  manifestId: string;
  /** Full display name (master table row). */
  name: string;
  /** Compact label (chips, breadcrumbs). */
  shortLabel: string;
  /** Statutory hook shown alongside the name. */
  provision: string;
  /** Base name for the Anthropic agent env-var triple (`<base>`, `<base>_DEV`, `<base>_VERSION`). */
  envBase: string;
  activityLogPrefix: string;
  promptKey: string;
  /**
   * Group B coupling (MOR-516): heads whose window moves 1:1 with the
   * estimated insolvency date. Re-armed when Financial Analysis or Books &
   * Records change, or the practitioner overrides the date. Group C heads
   * (false) ignore the insolvency date entirely.
   */
  dateSensitive: boolean;
  /**
   * Heads whose provision requires a purpose or fault element that the agent
   * does NOT assess — it surfaces the transactions, and the practitioner makes
   * the assessment on the circumstances and the evidence. Every flag must carry
   * `purposeBasis` (the circumstances observed) and `purposeEvidence` (what
   * material is on the file) so the recoverable is never read as settled.
   * Absent on heads whose elements the transaction record settles.
   */
  subjectiveElement?: {
    /** Element name shown on the chip / callout (e.g. "Purpose"). */
    label: string;
    /** Provision the element comes from (e.g. "s588FE(5)"). */
    provision: string;
  };
  /**
   * Heads whose recoverable is a LOSS the agent derives, not the sum of the
   * transactions it flags. On every other head the value moved out IS the
   * recoverable, so `createFlag` sets quantum to the exact member sum. Here the
   * transfers are only evidence: the claim is for the loss they caused, which
   * has its own ceiling. The agent must state the quantum, and it is capped.
   */
  derivedRecoverable?: {
    /** What the quantum measures, in the agent's words. */
    measure: string;
    /** Register whose total caps the recoverable. */
    ceiling: "employeeEntitlements";
  };
  /** Short blurb for the master table / workspace cards. */
  uiDescription: string;
}

/** Display order matches the sub-ticket numbering (MOR-515 … MOR-538). */
export const FLAGGED_HEAD_ORDER: ReadonlyArray<FlaggedHeadKey> = [
  "unfair_preferences",
  "related_entity_preference",
  "insolvent_trading",
  "director_related",
  "uncommercial",
  "related_entity_uncommercial",
  "unfair_loans",
  "defeat_creditors",
  "creditor_defeating_disposition",
  "defeat_employee_entitlements",
  "ppsa_vesting",
  "circulating_security_interest",
  "officer_security_interest",
  "bankruptcy_undervalued_transfer",
  "bankruptcy_preference",
  "bankruptcy_transfer_to_defeat_creditors",
];

export const FLAGGED_HEAD_CONFIGS: Record<FlaggedHeadKey, FlaggedHeadConfig> = {
  unfair_preferences: {
    headKey: "unfair_preferences",
    regime: "corporate",
    manifestId: "sum_flagged_unfair_preferences",
    name: "Unfair Preference Payments",
    shortLabel: "Preferences",
    provision: "s588FA / s588FE(2)",
    envBase: "ANTHROPIC_AGENT_FLAGGED_UNFAIR_PREFERENCES",
    activityLogPrefix: "[FlaggedPrefs]",
    promptKey: "managed.flagged.unfairPreferences.system",
    dateSensitive: true,
    uiDescription:
      "Payments that may prefer one creditor over others — comparable creditor treatment, catch-up and round-sum payments, acceleration toward appointment, and payments after pressure.",
  },
  related_entity_preference: {
    headKey: "related_entity_preference",
    regime: "corporate",
    manifestId: "sum_flagged_related_entity_preference",
    name: "Related-Entity Unfair Preferences",
    shortLabel: "Related-entity pref",
    provision: "s588FA / s588FE(4)",
    envBase: "ANTHROPIC_AGENT_FLAGGED_RELPREF",
    activityLogPrefix: "[FlaggedRelEntityPref]",
    promptKey: "managed.flagged.relatedEntityPreference.system",
    dateSensitive: true,
    uiDescription:
      "Unfair preferences paid to related entities over the extended 4-year window, including payments outside the general 6-month look-back.",
  },
  insolvent_trading: {
    headKey: "insolvent_trading",
    regime: "corporate",
    manifestId: "sum_flagged_insolvent_trading",
    name: "Insolvent Trading Claims",
    shortLabel: "Insolvent Trading",
    provision: "s588G / s588M",
    envBase: "ANTHROPIC_AGENT_FLAGGED_INSOLVENT_TRADING",
    activityLogPrefix: "[FlaggedInsTrading]",
    promptKey: "managed.flagged.insolventTrading.system",
    dateSensitive: true,
    uiDescription:
      "Debts incurred after the estimated insolvency date, attributed per director with s588M dividend-shortfall exposure and defence indicators.",
  },
  director_related: {
    headKey: "director_related",
    regime: "corporate",
    manifestId: "sum_flagged_director_related",
    name: "Unreasonable Director-related Transactions",
    shortLabel: "Director-related",
    provision: "s588FDA / s588FE(6A)",
    envBase: "ANTHROPIC_AGENT_FLAGGED_DIRECTOR_RELATED",
    activityLogPrefix: "[FlaggedDirector]",
    promptKey: "managed.flagged.directorRelated.system",
    dateSensitive: false,
    uiDescription:
      "Transfers, benefits and payments connected to directors or director-benefited parties — value-out vs value-in mismatch, no insolvency element.",
  },
  uncommercial: {
    headKey: "uncommercial",
    regime: "corporate",
    manifestId: "sum_flagged_uncommercial",
    name: "Uncommercial Transactions",
    shortLabel: "Uncommercial",
    provision: "s588FB / s588FE(3)",
    envBase: "ANTHROPIC_AGENT_FLAGGED_UNCOMMERCIAL",
    activityLogPrefix: "[FlaggedUncommercial]",
    promptKey: "managed.flagged.uncommercial.system",
    dateSensitive: true,
    uiDescription:
      "Transactions outside commercial terms, market value, or ordinary business rationale — detriment quantified as value out minus value in.",
  },
  related_entity_uncommercial: {
    headKey: "related_entity_uncommercial",
    regime: "corporate",
    manifestId: "sum_flagged_related_entity_uncommercial",
    name: "Related-Entity Uncommercial Transactions",
    shortLabel: "Related-entity",
    provision: "s588FB / s588FE(4)",
    envBase: "ANTHROPIC_AGENT_FLAGGED_RELUNC",
    activityLogPrefix: "[FlaggedRelEntity]",
    promptKey: "managed.flagged.relatedEntityUncommercial.system",
    dateSensitive: true,
    uiDescription:
      "Uncommercial transactions with related entities over the extended 4-year window, with counterparty-relationship evidence.",
  },
  unfair_loans: {
    headKey: "unfair_loans",
    regime: "corporate",
    manifestId: "sum_flagged_unfair_loans",
    name: "Unfair Loans",
    shortLabel: "Unfair Loans",
    provision: "s588FD / s588FE(6)",
    envBase: "ANTHROPIC_AGENT_FLAGGED_UNFAIR_LOANS",
    activityLogPrefix: "[FlaggedLoans]",
    promptKey: "managed.flagged.unfairLoans.system",
    dateSensitive: false,
    uiDescription:
      "Loan instruments with extortionate interest or charges relative to risk and security — no look-back period, related-party lenders prioritised.",
  },
  defeat_creditors: {
    headKey: "defeat_creditors",
    regime: "corporate",
    manifestId: "sum_flagged_defeat_creditors",
    name: "Extended Reach — Preferences & Uncommercial (10 years)",
    shortLabel: "Extended reach",
    provision: "s588FA / s588FB / s588FE(5)",
    envBase: "ANTHROPIC_AGENT_FLAGGED_DEFEAT_CREDITORS",
    activityLogPrefix: "[FlaggedDefeatCred]",
    promptKey: "managed.flagged.defeatCreditors.system",
    dateSensitive: true,
    subjectiveElement: { label: "Purpose", provision: "s588FE(5)" },
    uiDescription:
      "Asset strips, phoenix patterns, and transfers clustered around creditor pressure over the 10-year window — flagged for subjective intent review, never auto-concluded.",
  },
  creditor_defeating_disposition: {
    headKey: "creditor_defeating_disposition",
    regime: "corporate",
    manifestId: "sum_flagged_creditor_defeating",
    name: "Creditor-Defeating Dispositions",
    shortLabel: "CDD",
    provision: "s588FDB / s588FE(6B)",
    envBase: "ANTHROPIC_AGENT_FLAGGED_CDD",
    activityLogPrefix: "[FlaggedCDD]",
    promptKey: "managed.flagged.creditorDefeatingDisposition.system",
    dateSensitive: false,
    uiDescription:
      "Dispositions of company property for less than market value or best price reasonably obtainable, in the 12 months to the relation-back day — objective test, recovered from the recipient, no insolvency element.",
  },
  defeat_employee_entitlements: {
    headKey: "defeat_employee_entitlements",
    regime: "corporate",
    manifestId: "sum_flagged_defeat_employees",
    name: "Transactions to Defeat Employee Entitlements",
    shortLabel: "Employee Entitlements",
    provision: "s596AB / s596AC",
    envBase: "ANTHROPIC_AGENT_FLAGGED_DEFEAT_EMPLOYEES",
    activityLogPrefix: "[FlaggedDefeatEmp]",
    promptKey: "managed.flagged.defeatEmployees.system",
    dateSensitive: false,
    subjectiveElement: { label: "Fault element", provision: "s596AB" },
    derivedRecoverable: {
      measure:
        "the shortfall in employee entitlements attributable to the transaction",
      ceiling: "employeeEntitlements",
    },
    uiDescription:
      "Restructures, business sales, and workforce movements that left the employing entity unable to meet entitlements — entitlement-accrual timeline, no insolvency element.",
  },
  ppsa_vesting: {
    headKey: "ppsa_vesting",
    regime: "corporate",
    manifestId: "sum_flagged_ppsa_vesting",
    name: "Vesting of PPSA Security Interests",
    shortLabel: "PPSA Vesting",
    provision: "s588FL",
    envBase: "ANTHROPIC_AGENT_FLAGGED_PPSA_VESTING",
    activityLogPrefix: "[FlaggedPPSA]",
    promptKey: "managed.flagged.ppsaVesting.system",
    dateSensitive: false,
    uiDescription:
      "Registered PPSA security interests whose registration came inside the 6 months before the critical time and more than 20 business days after the security agreement — the interest vests in the company automatically, freeing the collateral and demoting the secured party to unsecured.",
  },
  circulating_security_interest: {
    headKey: "circulating_security_interest",
    regime: "corporate",
    manifestId: "sum_flagged_circulating_si",
    name: "Circulating Security Interests (6 Months)",
    shortLabel: "Circulating SI",
    provision: "s588FJ",
    envBase: "ANTHROPIC_AGENT_FLAGGED_CIRCULATING_SI",
    activityLogPrefix: "[FlaggedCircSI]",
    promptKey: "managed.flagged.circulatingSecurityInterest.system",
    dateSensitive: false,
    uiDescription:
      "Circulating security interests created in the 6 months to the relation-back day (and through to the day the winding up began) — void except so far as they secure protected value, so each interest carries a quantum: the unprotected portion.",
  },
  officer_security_interest: {
    headKey: "officer_security_interest",
    regime: "corporate",
    manifestId: "sum_flagged_officer_si",
    name: "Officer-Enforced Security Interests",
    shortLabel: "Officer SI",
    provision: "s588FP",
    envBase: "ANTHROPIC_AGENT_FLAGGED_OFFICER_SI",
    activityLogPrefix: "[FlaggedOfficerSI]",
    promptKey: "managed.flagged.officerSecurityInterest.system",
    dateSensitive: false,
    uiDescription:
      "Security interests held by an officer (or associate) that were enforced within 6 months of the instrument without Court leave — void ab initio, including the powers under the instrument; the collateral returns to the unsecured pool and the officer proves as unsecured.",
  },
  bankruptcy_undervalued_transfer: {
    headKey: "bankruptcy_undervalued_transfer",
    regime: "personal",
    manifestId: "sum_flagged_bky_undervalued_transfer",
    name: "Undervalued Transfers",
    shortLabel: "Undervalued transfers",
    provision: "Bankruptcy Act s 120",
    envBase: "ANTHROPIC_AGENT_FLAGGED_BKY_UNDERVALUED",
    activityLogPrefix: "[BkyUndervalued]",
    promptKey: "managed.flagged.bankruptcyUndervaluedTransfer.system",
    dateSensitive: false,
    uiDescription:
      "Transfers of property in the 5 years before commencement of bankruptcy for no consideration or less than market value. Solvency at the time is a safe harbour the transferee must prove; the trustee refunds any consideration given.",
  },
  bankruptcy_preference: {
    headKey: "bankruptcy_preference",
    regime: "personal",
    manifestId: "sum_flagged_bky_preference",
    name: "Avoidance of Preferences",
    shortLabel: "Preferences",
    provision: "Bankruptcy Act s 122",
    envBase: "ANTHROPIC_AGENT_FLAGGED_BKY_PREFERENCE",
    activityLogPrefix: "[BkyPreference]",
    promptKey: "managed.flagged.bankruptcyPreference.system",
    dateSensitive: true,
    uiDescription:
      "Payments and transfers to a creditor while the bankrupt was insolvent that gave that creditor a preference over others, in the 6 months before presentation of the petition (or from commencement where a creditor's petition was pending). Tax payments are protected.",
  },
  bankruptcy_transfer_to_defeat_creditors: {
    headKey: "bankruptcy_transfer_to_defeat_creditors",
    regime: "personal",
    manifestId: "sum_flagged_bky_defeat_creditors",
    name: "Transfers to Defeat Creditors",
    shortLabel: "Defeat creditors",
    provision: "Bankruptcy Act s 121",
    envBase: "ANTHROPIC_AGENT_FLAGGED_BKY_DEFEAT_CREDITORS",
    activityLogPrefix: "[BkyDefeatCred]",
    promptKey: "managed.flagged.bankruptcyDefeatCreditors.system",
    dateSensitive: true,
    subjectiveElement: { label: "Main purpose", provision: "s 121(1)(b)" },
    uiDescription:
      "Transfers at any time whose main purpose was to prevent, hinder or delay property becoming available to creditors. Purpose is taken to be made out where insolvency at the time can reasonably be inferred (s 121(2)).",
  },
};

const HEAD_BY_MANIFEST_ID = new Map<string, FlaggedHeadConfig>(
  Object.values(FLAGGED_HEAD_CONFIGS).map((c) => [c.manifestId, c]),
);

export function headConfigForManifestId(
  manifestId: string,
): FlaggedHeadConfig | undefined {
  return HEAD_BY_MANIFEST_ID.get(manifestId);
}

export function regimeForAppointmentType(
  type: AppointmentType | undefined,
): FlaggedRegime {
  return isPersonalInsolvencyAppointmentType(type) ? "personal" : "corporate";
}

export function headsForRegime(
  regime: FlaggedRegime,
): ReadonlyArray<FlaggedHeadKey> {
  return FLAGGED_HEAD_ORDER.filter(
    (k) => FLAGGED_HEAD_CONFIGS[k].regime === regime,
  );
}

/** The heads a job may schedule. Gate at every scheduler entry point. */
export function headsForJob(
  job: Pick<Doc<"jobs">, "appointmentType">,
): ReadonlyArray<FlaggedHeadKey> {
  return headsForRegime(regimeForAppointmentType(job.appointmentType));
}

export const BANKRUPTCY_HEAD_KEYS: ReadonlyArray<FlaggedHeadKey> =
  headsForRegime("personal");

/** All head keys whose window depends on the estimated insolvency date. */
export const DATE_SENSITIVE_HEADS: ReadonlyArray<FlaggedHeadKey> =
  FLAGGED_HEAD_ORDER.filter((k) => FLAGGED_HEAD_CONFIGS[k].dateSensitive);

/**
 * Sibling-agent reactivity: upstream manifest id → flagged heads to re-arm
 * when that agent completes a successful (non-chat) run. Consumed by
 * `runner.ts` alongside the BOOKS_AND_RECORDS_UPSTREAM pattern.
 *
 * Derived from each sub-ticket's DATA TO REVIEW list. Date-sensitive heads
 * additionally re-arm on Financial Analysis / Books & Records changes (the
 * "Group B" 1:1 coupling called out in MOR-516).
 */
export const FLAGGED_UPSTREAM: Record<string, ReadonlyArray<FlaggedHeadKey>> = {
  sum_bank_transactions: [
    "unfair_preferences",
    "related_entity_preference",
    "director_related",
    "uncommercial",
    "related_entity_uncommercial",
    "unfair_loans",
    "defeat_creditors",
    "creditor_defeating_disposition",
    "defeat_employee_entitlements",
    "circulating_security_interest",
    "officer_security_interest",
    "bankruptcy_undervalued_transfer",
    "bankruptcy_preference",
    "bankruptcy_transfer_to_defeat_creditors",
  ],
  sum_financial_analysis: [...DATE_SENSITIVE_HEADS],
  sum_books_and_records: [...DATE_SENSITIVE_HEADS],
  sum_related_parties: [
    "unfair_preferences",
    "related_entity_preference",
    "director_related",
    "related_entity_uncommercial",
    "unfair_loans",
    "defeat_creditors",
    "creditor_defeating_disposition",
    "circulating_security_interest",
    "officer_security_interest",
    "bankruptcy_undervalued_transfer",
    "bankruptcy_preference",
    "bankruptcy_transfer_to_defeat_creditors",
  ],
  sum_security_interests: [
    "unfair_preferences",
    "related_entity_preference",
    "insolvent_trading",
    "uncommercial",
    "related_entity_uncommercial",
    "unfair_loans",
    "defeat_creditors",
    "ppsa_vesting",
    "circulating_security_interest",
    "officer_security_interest",
  ],
  sum_loans: [
    "related_entity_preference",
    "director_related",
    "uncommercial",
    "related_entity_uncommercial",
    "unfair_loans",
    "defeat_creditors",
    "creditor_defeating_disposition",
    "defeat_employee_entitlements",
    "ppsa_vesting",
    "circulating_security_interest",
    "officer_security_interest",
  ],
  sum_corporate: [
    "insolvent_trading",
    "director_related",
    "defeat_creditors",
    "creditor_defeating_disposition",
    "defeat_employee_entitlements",
    "ppsa_vesting",
    "officer_security_interest",
  ],
  sum_employment: ["insolvent_trading", "defeat_employee_entitlements"],
  sum_liabilities: [
    "unfair_preferences",
    "insolvent_trading",
    "ppsa_vesting",
    "circulating_security_interest",
    "officer_security_interest",
    "bankruptcy_undervalued_transfer",
    "bankruptcy_preference",
    "bankruptcy_transfer_to_defeat_creditors",
  ],
  sum_asset_list: [
    "related_entity_preference",
    "director_related",
    "uncommercial",
    "related_entity_uncommercial",
    "defeat_creditors",
    "creditor_defeating_disposition",
    "defeat_employee_entitlements",
    "ppsa_vesting",
    "circulating_security_interest",
    "officer_security_interest",
    "bankruptcy_undervalued_transfer",
    "bankruptcy_preference",
    "bankruptcy_transfer_to_defeat_creditors",
  ],
  sum_asset_sales: [
    "related_entity_preference",
    "director_related",
    "uncommercial",
    "related_entity_uncommercial",
    "defeat_creditors",
    "creditor_defeating_disposition",
    "defeat_employee_entitlements",
    "circulating_security_interest",
    "officer_security_interest",
    "bankruptcy_undervalued_transfer",
    "bankruptcy_preference",
    "bankruptcy_transfer_to_defeat_creditors",
  ],
  sum_ato_sro: ["insolvent_trading", "defeat_employee_entitlements"],
  sum_board_reports: [
    "insolvent_trading",
    "related_entity_preference",
    "director_related",
    "uncommercial",
    "related_entity_uncommercial",
    "officer_security_interest",
  ],
};

/**
 * Heads whose transactions can be an insolvent transaction under s588FC — i.e.
 * an unfair preference (s588FA) or an uncommercial transaction (s588FB). ONLY
 * these can also found the extended-reach s588FE(5) claim, so only they may
 * carry `defeat_creditors` as a parallel.
 *
 * s588FD (unfair loans), s588FDA (director-related), s588FDB (creditor-
 * defeating dispositions), s588FJ/s588FP (security interests), s588FL (PPSA
 * vesting) and s588G (insolvent trading) do NOT run through s588FC, so a
 * parallel FROM one of those heads TO `defeat_creditors` is invalid — those
 * heads can be reached from a characterised s588FA/s588FB transaction, but they
 * cannot make a transaction into a s588FE(5) candidate.
 */
const EXTENDED_REACH_SOURCE_HEADS: ReadonlySet<FlaggedHeadKey> = new Set([
  "unfair_preferences",
  "related_entity_preference",
  "uncommercial",
  "related_entity_uncommercial",
]);

/**
 * General → related-entity routing for parallels. The general preference and
 * uncommercial heads do not review related-party counterparties (those are
 * owned end to end by the s588FE(4) heads), so a parallel from a related-party
 * flag to a general head points the practitioner at an empty page (MOR-1265).
 */
const RELATED_PARALLEL_REMAP: Partial<Record<FlaggedHeadKey, FlaggedHeadKey>> =
  {
    unfair_preferences: "related_entity_preference",
    uncommercial: "related_entity_uncommercial",
  };

/**
 * Normalise agent-supplied parallels before a flag is persisted.
 *
 * Rules:
 * 1. (MOR-1264) a `defeat_creditors` parallel is only valid from a head that
 *    can found the claim (see {@link EXTENDED_REACH_SOURCE_HEADS}).
 * 2. (MOR-1265) when the counterparty is a related party, a general preference
 *    or uncommercial parallel is rewritten to its related-entity sibling.
 * 3. A parallel that ends up pointing at the source head itself is dropped,
 *    and duplicates are collapsed.
 *
 * @param sourceHeadKey - the head the flag is being written under.
 * @param parallels - the agent-supplied `parallelHeadFlags`, if any.
 * @param related - whether the counterparty is a related party (a relationship
 *   hint from the register / officer matcher, or one the agent recorded).
 * @returns the normalised list, or `undefined` when nothing remains.
 */
export function sanitizeParallelHeadFlags(
  sourceHeadKey: FlaggedHeadKey,
  parallels: ReadonlyArray<FlaggedHeadKey> | undefined,
  related: boolean = false,
): Array<FlaggedHeadKey> | undefined {
  if (!parallels || parallels.length === 0) return undefined;
  const cleaned = parallels
    .filter(
      (target) =>
        target !== "defeat_creditors" ||
        EXTENDED_REACH_SOURCE_HEADS.has(sourceHeadKey),
    )
    .map((target) =>
      related ? (RELATED_PARALLEL_REMAP[target] ?? target) : target,
    )
    .filter((target) => target !== sourceHeadKey);
  const unique = Array.from(new Set(cleaned));
  return unique.length > 0 ? unique : undefined;
}

/**
 * True when a flag written under the extended-reach head (s588FE(5)) records
 * the s588FA / s588FB character the write path now requires. Flags on every
 * other head are treated as characterised (the question does not arise).
 *
 * Read-time only (MOR-1264 acceptance criterion 3): rows written before the
 * character became a gate have no `character` and are surfaced for review
 * rather than migrated.
 */
export function hasExtendedReachCharacter(flag: {
  headKey: FlaggedHeadKey;
  character?: string;
  characterBasis?: string;
}): boolean {
  if (flag.headKey !== "defeat_creditors") return true;
  return (
    Boolean(flag.character) && (flag.characterBasis ?? "").trim().length >= 20
  );
}
